import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { Token, LoginCredentials, RegisterData, User, Recording, RecordingListResponse } from '../types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

/**
 * Format error message from axios error
 */
function formatErrorMessage(error: any): string {
  // Network error (no response from server)
  if (!error.response) {
    if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
      return 'Verbindungsfehler: Server nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.'
    }
    if (error.code === 'ECONNREFUSED') {
      return 'Verbindung abgelehnt: Backend-Server läuft möglicherweise nicht.'
    }
    if (error.message?.includes('CORS')) {
      return 'CORS-Fehler: Backend erlaubt keine Anfragen von diesem Origin.'
    }
    return `Netzwerkfehler: ${error.message || 'Unbekannter Fehler'}`
  }

  const status = error.response.status
  const data = error.response.data

  // Handle different HTTP status codes
  switch (status) {
    case 400:
      return data?.detail || 'Ungültige Anfrage. Bitte prüfen Sie Ihre Eingaben.'
    case 401:
      return 'Nicht autorisiert. Bitte melden Sie sich erneut an.'
    case 403:
      return data?.detail || 'Zugriff verweigert. Sie haben keine Berechtigung für diese Aktion.'
    case 404:
      return 'Ressource nicht gefunden.'
    case 422:
      // Validation errors
      if (Array.isArray(data?.detail)) {
        return data.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
      }
      return data?.detail || 'Validierungsfehler.'
    case 500:
      return 'Serverfehler. Bitte versuchen Sie es später erneut.'
    case 503:
      return 'Service nicht verfügbar. Bitte versuchen Sie es später erneut.'
    default:
      return data?.detail || `Fehler ${status}: ${data?.message || 'Unbekannter Fehler'}`
  }
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: `${API_URL}/api/v1`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds timeout
    })

    // Add auth token to requests
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Handle response errors
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        // Format error message
        const formattedError = new Error(formatErrorMessage(error))
        
        // Handle 401 errors (unauthorized) - don't redirect on login/register pages
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname
          if (currentPath !== '/login' && currentPath !== '/register') {
            localStorage.removeItem('access_token')
            window.location.href = '/login'
          }
        }
        
        // Attach original error for debugging
        ;(formattedError as any).originalError = error
        return Promise.reject(formattedError)
      }
    )
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<Token> {
    try {
      // Use the JSON login endpoint
      const response = await this.client.post<Token>('/auth/login', credentials)
      return response.data
    } catch (error: any) {
      // Extract error message from response
      let errorMessage = 'Login fehlgeschlagen'
      if (error.response?.data?.detail) {
        errorMessage = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Ungültige Anmeldedaten'
      } else if (error.message) {
        errorMessage = formatErrorMessage(error)
      }
      throw new Error(errorMessage)
    }
  }

  async register(data: RegisterData): Promise<User> {
    try {
      const response = await this.client.post<User>('/auth/register', data)
      return response.data
    } catch (error: any) {
      // Re-throw with formatted error message
      throw new Error(formatErrorMessage(error))
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/auth/me')
    return response.data
  }

  // Audio endpoints
  async uploadAudio(file: File, onProgress?: (progress: number) => void): Promise<Recording> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.client.post<Recording>('/audio/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
    return response.data
  }

  async listRecordings(limit = 50, offset = 0): Promise<RecordingListResponse> {
    const response = await this.client.get<RecordingListResponse>('/audio/list', {
      params: { limit, offset },
    })
    return response.data
  }

  async getRecording(id: string): Promise<Recording> {
    const response = await this.client.get<Recording>(`/audio/${id}`)
    return response.data
  }

  getStreamUrl(id: string): string {
    return `${API_URL}/api/v1/audio/stream/${id}`
  }
}

export const apiClient = new ApiClient()

